import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentReturnPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState('checking'); // checking | success | expired | error | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 10) { setState('timeout'); return; }
      attempts.current += 1;
      try {
        const res = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        const data = res.data;
        setInfo(data);
        if (data.payment_status === 'paid') {
          setState('success');
          if (data.type === 'registration') {
            await checkAuth();
            setTimeout(() => navigate('/dashboard'), 2500);
          } else if (data.type === 'book_order' && data.book_id) {
            setTimeout(() => navigate(`/books/${data.book_id}`), 2500);
          } else if (data.type === 'additional_book_credit') {
            setTimeout(() => navigate('/books/new'), 2500);
          } else if (data.type === 'additional_book') {
            setTimeout(() => navigate(data.book_id ? `/books/${data.book_id}` : '/dashboard'), 2500);
          } else if (data.type === 'contributor_purchase' && data.invite_token) {
            setTimeout(() => navigate(`/contribute/${data.invite_token}?compra=ok`), 2500);
          }
          return;
        }
        if (data.status === 'expired') { setState('expired'); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState('error');
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const successMessage = info?.type === 'registration'
    ? 'Tu cuenta está activada. Te llevamos a tu panel para empezar tu libro…'
    : info?.type === 'book_order'
      ? 'Tu pedido está confirmado y el libro pasa a preparación para imprenta. Te llevamos de vuelta…'
      : info?.type === 'additional_book_credit'
        ? 'Tu nuevo libro está pagado. Te llevamos al asistente para configurarlo…'
        : info?.type === 'additional_book'
        ? 'Tu nuevo libro está pagado y creado. Te llevamos a él para que empieces…'
        : 'Tu ejemplar está confirmado. Te llevamos de vuelta…';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="payment-return-page">
      <Card className="card-elevated max-w-md w-full">
        <CardContent className="py-10 text-center space-y-4">
          {state === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" data-testid="payment-checking" />
              <h1 className="font-heading text-xl font-bold">Verificando tu pago…</h1>
              <p className="text-sm text-muted-foreground">
                Estamos confirmando el pago con Stripe. No cierres esta ventana.
              </p>
            </>
          )}
          {state === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-9 h-9 text-emerald-600" />
              </div>
              <h1 className="font-heading text-xl font-bold text-emerald-900" data-testid="payment-success">¡Pago completado!</h1>
              <p className="text-sm text-muted-foreground">{successMessage}</p>
              {info?.amount_eur != null && (
                <p className="text-xs text-muted-foreground">Importe: <strong>{Number(info.amount_eur).toFixed(2)}€</strong></p>
              )}
            </>
          )}
          {(state === 'error' || state === 'expired') && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <XCircle className="w-9 h-9 text-red-600" />
              </div>
              <h1 className="font-heading text-xl font-bold" data-testid="payment-error">
                {state === 'expired' ? 'La sesión de pago ha caducado' : 'No pudimos verificar el pago'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {state === 'expired'
                  ? 'Puedes volver a intentarlo desde tu panel.'
                  : 'Si el cargo se ha realizado, tu cuenta se activará en unos minutos. Si no, inténtalo de nuevo.'}
              </p>
              <Button asChild className="btn-primary" data-testid="payment-back-btn">
                <Link to="/dashboard">Ir a mi panel</Link>
              </Button>
            </>
          )}
          {state === 'timeout' && (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Clock className="w-9 h-9 text-amber-600" />
              </div>
              <h1 className="font-heading text-xl font-bold" data-testid="payment-timeout">Seguimos verificando tu pago</h1>
              <p className="text-sm text-muted-foreground">
                Está tardando más de lo habitual. Recibirás un email en cuanto se confirme,
                o vuelve a tu panel en unos minutos.
              </p>
              <Button asChild variant="outline" data-testid="payment-timeout-back-btn">
                <Link to="/dashboard">Ir a mi panel</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
